import { useCallback, useEffect, useRef } from 'react';

import { useAppDispatch } from '@/configuration/setup/hooks';
import { TABLE_TO_API_MAP } from '@/features/common/components/dialogs/dialogConstants';
import { dialogApi, useGetDialogDataQuery, usePutDialogDataMutation } from '@/services/dialogsApi';

const SAVE_DEBOUNCE_MS = 1000;

export const USER_SETTINGS_BROADCAST_CHANNEL = 'vehicle-diagnostics-user-settings-channel';
/**
 * Manages user settings for a given table type, providing persistent read/write
 * access via the dialog API and cross-tab synchronisation via BroadcastChannel.
 *
 * BroadcastChannel (`USER_SETTINGS_BROADCAST_CHANNEL`):
 * - Listens for settings updates broadcast from other tabs/windows (UserSettingsDialog in our scenario).
 * - On message, patches the RTK Query cache directly via `updateQueryData`,
 *   keeping all open iframes
 *   with the latest data, even if they haven't re-fetched.
 *
 * RTK Query Cache:
 * - The `getDialogData` query is used to fetch and store the current settings.
 * - The `putDialogData` mutation is used to save changes to the backend.
 * - The `updateQueryData` helper is used to optimistically update the cache
 *   when a settings update is received via BroadcastChannel, keeping the UI
 *   in sync with the latest data.
 *
 * Debounced Saves:
 * - Changes to settings are debounced to avoid excessive API calls.
 * - The `saveSettings` function is used to update the settings locally and
 *   trigger a debounced save to the backend.
 *
 */
export const useUserSettings = <T>(tableType: string) => {
    const apiPath = TABLE_TO_API_MAP[tableType as keyof typeof TABLE_TO_API_MAP];
    const {
        data: settings,
        isError: isSettingsError,
        refetch,
    } = useGetDialogDataQuery({ path: apiPath }, { skip: !apiPath });
    const [putSettings] = usePutDialogDataMutation();
    const debounceTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
    const channelRef = useRef<BroadcastChannel | null>(null);
    const dispatch = useAppDispatch();
    const saveSettings = useCallback(
        (patch: Partial<T>) => {
            if (!settings || !apiPath) {
                return;
            }
            clearTimeout(debounceTimer.current);
            debounceTimer.current = setTimeout(() => {
                const patched = { ...settings, ...patch };
                if (shouldSkipPut(settings, patched)) {
                    return;
                }
                putSettings({ path: apiPath, data: patched });
            }, SAVE_DEBOUNCE_MS);
        },
        [settings, apiPath, putSettings],
    );

    useEffect(() => {
        const channel = new BroadcastChannel(USER_SETTINGS_BROADCAST_CHANNEL);
        channel.onmessage = (event: MessageEvent) => {
            dispatch(
                dialogApi.util.updateQueryData('getDialogData', { path: apiPath }, () => event.data.payload.settings),
            );
        };
        channelRef.current = channel;
        return () => {
            channel.close();
            channelRef.current = null;
        };
    }, [apiPath]);

    useEffect(() => () => clearTimeout(debounceTimer.current), []);

    return {
        settings: settings as T | undefined,
        saveSettings,
        isSettingsError: isSettingsError || !apiPath,
        refetch,
    };
};

/**
 * Compares two plain JSON-serializable objects by value.
 * Safe because `next` is always built as `{ ...current, ...patch }`,
 * guaranteeing consistent key order between the two.
 */
const shouldSkipPut = (current: unknown, next: unknown) => JSON.stringify(current) === JSON.stringify(next);
